// ============================================================================
//  BYPASS CODE SERVICE

// ============================================================================
// Handles generation, validation, and management of voice/video chat bypass codes
// - Agents generate their own private codes (separate from referral codes)
// - Admins can create and manage all codes
// - Unlimited use per code, usage tracked for stats
// ============================================================================

import { db } from './firebase';
import gameApiService from './gameApiService';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  increment,
  Timestamp,
} from 'firebase/firestore';

// ============================================================================
// TYPES
// ============================================================================

export interface BypassCode {
  id: string;
  code: string;
  ownerId: string;
  ownerName: string;
  ownerType: 'agent' | 'admin';
  active: boolean;
  usageCount: number;
  createdAt: Date;
  updatedAt: Date;
  lastUsedAt?: Date;
  lastUsedBy?: string;
  description?: string;
}

export interface BypassCodeValidationResult {
  valid: boolean;
  free: boolean;
  codeId?: string;
  ownerId?: string;
  ownerName?: string;
  ownerType?: 'agent' | 'admin';
  error?: string;
}

export interface BypassCodeStats {
  totalCodes: number;
  activeCodes: number;
  totalUsage: number;
  codesThisMonth: number;
}

export interface CreateBypassCodeParams {
  ownerId: string;
  ownerName: string;
  ownerType: 'agent' | 'admin';
  description?: string;
}

export interface BypassCodeUsageLog {
  id: string;
  codeId: string;
  code: string;
  usedBy: string;
  usedByName?: string;
  tableId: string;
  tableName?: string;
  usedAt: Date;
  ownerId: string;
  ownerName: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const BYPASS_CODES_COLLECTION = 'bypassCodes';
const BYPASS_CODE_USAGE_COLLECTION = 'bypassCodeUsage';

// Code format: 8 characters, alphanumeric, easy to read (no confusing chars)
const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const CODE_LENGTH = 8;

// ============================================================================
// BYPASS CODE SERVICE CLASS
// ============================================================================

class BypassCodeService {
  // ==========================================================================
  // CODE GENERATION
  // ==========================================================================

  /**
   * Generate a unique random bypass code
   * Format: 8 alphanumeric characters (e.g., "VIP8XK3M")
   */
  private generateCodeString(): string {
    let code = '';
    for (let i = 0; i < CODE_LENGTH; i++) {
      const randomIndex = Math.floor(Math.random() * CODE_CHARS.length);
      code += CODE_CHARS[randomIndex];
    }
    return code;
  }

  /**
   * Check if a code already exists in the database
   */
  private async codeExists(code: string): Promise<boolean> {
    const q = query(
      collection(db, BYPASS_CODES_COLLECTION),
      where('code', '==', code.toUpperCase()),
      limit(1)
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  }

  /**
   * Generate a unique code (retries if collision)
   */
  private async generateUniqueCode(): Promise<string> {
    let attempts = 0;
    const maxAttempts = 10;

    while (attempts < maxAttempts) {
      const code = this.generateCodeString();
      const exists = await this.codeExists(code);
      
      if (!exists) {
        return code;
      }
      
      attempts++;
      console.log(`⚠️ Code collision, retrying (attempt ${attempts})`);
    }

    // Extremely unlikely to reach here
    throw new Error('Failed to generate unique code after multiple attempts');
  }

  // ==========================================================================
  // CODE CREATION
  // ==========================================================================

  /**
   * Create a new bypass code for an agent or admin
   */
  async createBypassCode(params: CreateBypassCodeParams): Promise<BypassCode> {
    // Issued by the server: it enforces the monthly cap for agents, signs the
    // code, and sets expiry and use limits. Unsigned codes are ignored at use time.
    const r = await gameApiService.bypassCodeCreate(params.description);
    return {
      id: r.id,
      code: r.code,
      ownerId: r.ownerId,
      ownerName: r.ownerName,
      ownerType: r.ownerType,
      active: true,
      usageCount: 0,
      description: r.description || '',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  }

  /**
   * Create a bypass code for an agent (called from AgentDashboard)
   */
  async createAgentBypassCode(agentId: string, agentName: string, description?: string): Promise<BypassCode> {
    return this.createBypassCode({
      ownerId: agentId,
      ownerName: agentName,
      ownerType: 'agent',
      description,
    });
  }

  /**
   * Create a bypass code for an admin (called from AdminDashboard)
   */
  async createAdminBypassCode(adminId: string, adminName: string, description?: string): Promise<BypassCode> {
    return this.createBypassCode({
      ownerId: adminId,
      ownerName: adminName,
      ownerType: 'admin',
      description,
    });
  }

  // ==========================================================================
  // CODE VALIDATION
  // ==========================================================================

  /**
   * Validate a bypass code
   * Returns validation result with owner info if valid
   */
  async validateCode(code: string): Promise<BypassCodeValidationResult> {
    if (!code || code.trim().length < 3) {
      return { valid: false, free: false, error: 'Invalid code format' };
    }
    try {
      // Server check: signature, expiry, use cap, owner still a partner
      const r = await gameApiService.bypassCodeCheck(code.trim().toUpperCase());
      if (!r.valid) return { valid: false, free: false, error: r.error || 'Code not found or inactive' };
      return { valid: true, free: true, codeId: r.codeId, ownerId: r.ownerId, ownerName: r.ownerName, ownerType: r.ownerType };
    } catch (error) {
      console.error('❌ Error validating bypass code:', error);
      return { valid: false, free: false, error: 'Failed to validate code' };
    }
  }

  /**
   * Record usage of a bypass code
   * Called when someone successfully uses a code to create a voice/video table
   */
  async recordCodeUsage(
    codeId: string,
    code: string,
    usedBy: string,
    usedByName: string,
    tableId: string,
    tableName?: string
  ): Promise<void> {
    try {
      // Get code info for owner details
      const codeDoc = await getDoc(doc(db, BYPASS_CODES_COLLECTION, codeId));
      
      if (!codeDoc.exists()) {
        console.warn('⚠️ Code not found for usage recording:', codeId);
        return;
      }

      const codeData = codeDoc.data();

      // Update usage count on the code
      await updateDoc(doc(db, BYPASS_CODES_COLLECTION, codeId), {
        usageCount: increment(1),
        lastUsedAt: serverTimestamp(),
        lastUsedBy: usedBy,
        updatedAt: serverTimestamp(),
      });

      // Log the usage
      await addDoc(collection(db, BYPASS_CODE_USAGE_COLLECTION), {
        codeId,
        code: code.toUpperCase(),
        usedBy,
        usedByName,
        tableId,
        tableName: tableName || '',
        usedAt: serverTimestamp(),
        ownerId: codeData.ownerId,
        ownerName: codeData.ownerName,
        ownerType: codeData.ownerType,
      });

      console.log(`📊 Recorded bypass code usage: ${code} by ${usedByName}`);
    } catch (error) {
      console.error('❌ Error recording code usage:', error);
      // Don't throw - usage recording is not critical
    }
  }

  // ==========================================================================
  // CODE MANAGEMENT
  // ==========================================================================

  /**
   * Get all bypass codes for an owner (agent or admin)
   */
  async getCodesByOwner(ownerId: string): Promise<BypassCode[]> {
    try {
      const q = query(
        collection(db, BYPASS_CODES_COLLECTION),
        where('ownerId', '==', ownerId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const codes: BypassCode[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        codes.push({
          id: docSnap.id,
          code: data.code,
          ownerId: data.ownerId,
          ownerName: data.ownerName,
          ownerType: data.ownerType,
          active: data.active,
          usageCount: data.usageCount || 0,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          lastUsedAt: data.lastUsedAt?.toDate(),
          lastUsedBy: data.lastUsedBy,
          description: data.description,
        });
      });

      return codes;
    } catch (error) {
      console.error('❌ Error getting codes by owner:', error);
      return [];
    }
  }

  /**
   * Get all bypass codes (admin only)
   */
  async getAllCodes(): Promise<BypassCode[]> {
    try {
      const q = query(
        collection(db, BYPASS_CODES_COLLECTION),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const codes: BypassCode[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        codes.push({
          id: docSnap.id,
          code: data.code,
          ownerId: data.ownerId,
          ownerName: data.ownerName,
          ownerType: data.ownerType,
          active: data.active,
          usageCount: data.usageCount || 0,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          lastUsedAt: data.lastUsedAt?.toDate(),
          lastUsedBy: data.lastUsedBy,
          description: data.description,
        });
      });

      return codes;
    } catch (error) {
      console.error('❌ Error getting all codes:', error);
      return [];
    }
  }

  /**
   * Get usage logs for a specific code
   */
  async getCodeUsageLogs(codeId: string, limitCount: number = 50): Promise<BypassCodeUsageLog[]> {
    try {
      const q = query(
        collection(db, BYPASS_CODE_USAGE_COLLECTION),
        where('codeId', '==', codeId),
        orderBy('usedAt', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      const logs: BypassCodeUsageLog[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        logs.push({
          id: docSnap.id,
          codeId: data.codeId,
          code: data.code,
          usedBy: data.usedBy,
          usedByName: data.usedByName,
          tableId: data.tableId,
          tableName: data.tableName,
          usedAt: data.usedAt?.toDate() || new Date(),
          ownerId: data.ownerId,
          ownerName: data.ownerName,
        });
      });

      return logs;
    } catch (error) {
      console.error('❌ Error getting usage logs:', error);
      return [];
    }
  }

  /**
   * Get all usage logs for an owner (agent sees their own usage)
   */
  async getUsageLogsByOwner(ownerId: string, limitCount: number = 100): Promise<BypassCodeUsageLog[]> {
    try {
      const q = query(
        collection(db, BYPASS_CODE_USAGE_COLLECTION),
        where('ownerId', '==', ownerId),
        orderBy('usedAt', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      const logs: BypassCodeUsageLog[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        logs.push({
          id: docSnap.id,
          codeId: data.codeId,
          code: data.code,
          usedBy: data.usedBy,
          usedByName: data.usedByName,
          tableId: data.tableId,
          tableName: data.tableName,
          usedAt: data.usedAt?.toDate() || new Date(),
          ownerId: data.ownerId,
          ownerName: data.ownerName,
        });
      });

      return logs;
    } catch (error) {
      console.error('❌ Error getting usage logs by owner:', error);
      return [];
    }
  }

  /**
   * Deactivate a bypass code
   */
  async deactivateCode(codeId: string): Promise<boolean> {
    try {
      await updateDoc(doc(db, BYPASS_CODES_COLLECTION, codeId), {
        active: false,
        updatedAt: serverTimestamp(),
      });

      console.log(`🚫 Deactivated bypass code: ${codeId}`);
      return true;
    } catch (error) {
      console.error('❌ Error deactivating code:', error);
      return false;
    }
  }

  /**
   * Reactivate a bypass code
   */
  async reactivateCode(codeId: string): Promise<boolean> {
    try {
      await updateDoc(doc(db, BYPASS_CODES_COLLECTION, codeId), {
        active: true,
        updatedAt: serverTimestamp(),
      });

      console.log(`✅ Reactivated bypass code: ${codeId}`);
      return true;
    } catch (error) {
      console.error('❌ Error reactivating code:', error);
      return false;
    }
  }

  /**
   * Delete a bypass code permanently
   */
  async deleteCode(codeId: string): Promise<boolean> {
    try {
      await deleteDoc(doc(db, BYPASS_CODES_COLLECTION, codeId));

      console.log(`🗑️ Deleted bypass code: ${codeId}`);
      return true;
    } catch (error) {
      console.error('❌ Error deleting code:', error);
      return false;
    }
  }

  /**
   * Update code description
   */
  async updateCodeDescription(codeId: string, description: string): Promise<boolean> {
    try {
      await updateDoc(doc(db, BYPASS_CODES_COLLECTION, codeId), {
        description,
        updatedAt: serverTimestamp(),
      });

      return true;
    } catch (error) {
      console.error('❌ Error updating code description:', error);
      return false;
    }
  }

  // ==========================================================================
  // STATISTICS
  // ==========================================================================

  /**
   * Get bypass code statistics for an owner
   */
  async getOwnerStats(ownerId: string): Promise<BypassCodeStats> {
    try {
      const codes = await this.getCodesByOwner(ownerId);

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      let totalUsage = 0;
      let activeCodes = 0;
      let codesThisMonth = 0;

      codes.forEach((code) => {
        totalUsage += code.usageCount;
        if (code.active) activeCodes++;
        if (code.createdAt >= startOfMonth) codesThisMonth++;
      });

      return {
        totalCodes: codes.length,
        activeCodes,
        totalUsage,
        codesThisMonth,
      };
    } catch (error) {
      console.error('❌ Error getting owner stats:', error);
      return {
        totalCodes: 0,
        activeCodes: 0,
        totalUsage: 0,
        codesThisMonth: 0,
      };
    }
  }

  /**
   * Get global bypass code statistics (admin only)
   */
  async getGlobalStats(): Promise<BypassCodeStats & { 
    agentCodes: number; 
    adminCodes: number;
    totalUsageThisMonth: number;
  }> {
    try {
      const codes = await this.getAllCodes();

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      let totalUsage = 0;
      let activeCodes = 0;
      let codesThisMonth = 0;
      let agentCodes = 0;
      let adminCodes = 0;

      codes.forEach((code) => {
        totalUsage += code.usageCount;
        if (code.active) activeCodes++;
        if (code.createdAt >= startOfMonth) codesThisMonth++;
        if (code.ownerType === 'agent') agentCodes++;
        if (code.ownerType === 'admin') adminCodes++;
      });

      // Get this month's usage from logs
      const usageQuery = query(
        collection(db, BYPASS_CODE_USAGE_COLLECTION),
        where('usedAt', '>=', Timestamp.fromDate(startOfMonth)),
        orderBy('usedAt', 'desc')
      );
      const usageSnapshot = await getDocs(usageQuery);
      const totalUsageThisMonth = usageSnapshot.size;

      return {
        totalCodes: codes.length,
        activeCodes,
        totalUsage,
        codesThisMonth,
        agentCodes,
        adminCodes,
        totalUsageThisMonth,
      };
    } catch (error) {
      console.error('❌ Error getting global stats:', error);
      return {
        totalCodes: 0,
        activeCodes: 0,
        totalUsage: 0,
        codesThisMonth: 0,
        agentCodes: 0,
        adminCodes: 0,
        totalUsageThisMonth: 0,
      };
    }
  }

  // ==========================================================================
  // ADMIN FUNCTIONS
  // ==========================================================================

  /**
   * Get codes by type (agent or admin)
   */
  async getCodesByType(ownerType: 'agent' | 'admin'): Promise<BypassCode[]> {
    try {
      const q = query(
        collection(db, BYPASS_CODES_COLLECTION),
        where('ownerType', '==', ownerType),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const codes: BypassCode[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        codes.push({
          id: docSnap.id,
          code: data.code,
          ownerId: data.ownerId,
          ownerName: data.ownerName,
          ownerType: data.ownerType,
          active: data.active,
          usageCount: data.usageCount || 0,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          lastUsedAt: data.lastUsedAt?.toDate(),
          lastUsedBy: data.lastUsedBy,
          description: data.description,
        });
      });

      return codes;
    } catch (error) {
      console.error('❌ Error getting codes by type:', error);
      return [];
    }
  }

  /**
   * Search codes by code string or owner name
   */
  async searchCodes(searchTerm: string): Promise<BypassCode[]> {
    try {
      // Get all codes and filter client-side
      // (Firestore doesn't support case-insensitive contains search)
      const allCodes = await this.getAllCodes();
      const term = searchTerm.toLowerCase();

      return allCodes.filter(
        (code) =>
          code.code.toLowerCase().includes(term) ||
          code.ownerName.toLowerCase().includes(term) ||
          (code.description && code.description.toLowerCase().includes(term))
      );
    } catch (error) {
      console.error('❌ Error searching codes:', error);
      return [];
    }
  }

  /**
   * Bulk deactivate codes by owner
   */
  async deactivateAllByOwner(ownerId: string): Promise<number> {
    try {
      const codes = await this.getCodesByOwner(ownerId);
      let deactivated = 0;

      for (const code of codes) {
        if (code.active) {
          await this.deactivateCode(code.id);
          deactivated++;
        }
      }

      console.log(`🚫 Bulk deactivated ${deactivated} codes for owner: ${ownerId}`);
      return deactivated;
    } catch (error) {
      console.error('❌ Error bulk deactivating codes:', error);
      return 0;
    }
  }

  /**
   * Get recent usage across all codes (admin dashboard)
   */
  async getRecentUsage(limitCount: number = 50): Promise<BypassCodeUsageLog[]> {
    try {
      const q = query(
        collection(db, BYPASS_CODE_USAGE_COLLECTION),
        orderBy('usedAt', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      const logs: BypassCodeUsageLog[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        logs.push({
          id: docSnap.id,
          codeId: data.codeId,
          code: data.code,
          usedBy: data.usedBy,
          usedByName: data.usedByName,
          tableId: data.tableId,
          tableName: data.tableName,
          usedAt: data.usedAt?.toDate() || new Date(),
          ownerId: data.ownerId,
          ownerName: data.ownerName,
        });
      });

      return logs;
    } catch (error) {
      console.error('❌ Error getting recent usage:', error);
      return [];
    }
  }
}

// ============================================================================
// EXPORT
// ============================================================================

export const bypassCodeService = new BypassCodeService();
export default bypassCodeService;